import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import BrandMark from './common/BrandMark';
import {
  GraduationCap,
  Briefcase,
  FileText,
  BarChart3,
  MapPin,
  Mail,
  Download,
  Sparkles,
  Maximize2,
  X,
  Search,
  ArrowLeft,
  Users,
  ShieldCheck,
  Languages,
  Clock
} from 'lucide-react';

const BRAND_COLORS = {
  primary: '#1a1a1a',
  secondary: '#7C3AED',
  accent: '#C9A961',
  dark: '#000000',
  light: '#F5F5F0',
  lightGray: '#E8E8E3',
  mediumGray: '#A0A09C',
  graphite: '#4A4A46',
  accentDeep: '#7F6224',
  pencil: '#6E6E67',
  mutedOnDark: '#B3B3AD',
  subtleOnDark: '#A8A8A2',
};

const HAIRLINE_GOLD = 'rgba(201,169,97,0.32)';
const HAIRLINE_WHITE = 'rgba(255,255,255,0.12)';

const CATEGORIES = [
  { id: 'all', label: 'All Disciplines' },
  { id: 'compliance', label: 'Compliance & Legal' },
  { id: 'pharmacy', label: 'Pharmacy & Healthcare' },
  { id: 'retail', label: 'Retail & Operations' },
];

const JOB_OPPORTUNITIES = [
  {
    id: 'part-time-sales-executive-hyderabad',
    title: 'Part-Time Sales Executive',
    category: 'retail',
    headline: 'Studying? Work evening and weekend shifts and pay your own fees.',
    companyType: 'A Reputed Women’s Apparel Store',
    companyCategory: 'Women’s Apparel & Activewear · Sarath City Capital Mall',
    location: 'Hyderabad',
    fullLocation: 'Sarath City Capital Mall, Hyderabad · On-site',
    type: 'Part-Time',
    postedDate: 'Recently Posted',
    isFeatured: true,
    brochureImage: '/jobs/part-time-sales-executive-hyderabad.jpeg',
    brochureTitle: 'Part-Time Sales Executive, Hyderabad — Recruitment Flyer',
    brochureDownloadName: 'SkillVend_Part_Time_Sales_Executive_Hyderabad.jpeg',
    description: 'A reputed women’s apparel store at Sarath City Capital Mall is hiring customer-focused, well-groomed part-time sales executives. You will help customers find the right products, keep the store looking its best, and contribute to the store’s sales targets. Open to women candidates; college students are welcome to apply.',
    highlights: [
      {
        icon: GraduationCap,
        label: 'Education',
        value: '10th / 12th Pass',
        subtext: 'Graduates and college students can also apply',
      },
      {
        icon: Briefcase,
        label: 'Experience',
        value: 'Freshers Welcome',
        subtext: 'Retail sales experience is an advantage',
      },
      {
        icon: Languages,
        label: 'Language',
        value: 'Telugu Essential',
        subtext: 'Good communication and customer-handling skills',
      },
      {
        icon: Clock,
        label: 'Working Hours',
        value: 'Part-Time Shifts',
        subtext: 'As per store schedule, including weekends and holidays',
      },
    ],
    responsibilities: [
      'Greeting customers and providing friendly, professional service.',
      'Understanding customer needs and recommending suitable products.',
      'Explaining product features, pricing, offers and promotions.',
      'Supporting sales conversion and working towards assigned sales targets.',
      'Maintaining product displays, folding, sizing and store cleanliness.',
      'Assisting with stock arrangement, replenishment and billing when required.',
      'Coordinating with the Store Manager and store staff, and following store policies.',
    ],
    applyEmail: 'helpdesk@skillvend.com',
    applySubject: 'Application - Part-Time Sales Executive, Sarath City Capital Mall Hyderabad',
    applyPhone: '9847510101',
    whatsappMessage: 'Hi SkillVend Team, I am interested in applying for the Part-Time Sales Executive role at Sarath City Capital Mall, Hyderabad.',
  },
  {
    id: 'part-time-sales-executive-coimbatore',
    title: 'Part-Time Sales Executive',
    category: 'retail',
    headline: 'Studying? Work evening and weekend shifts and pay your own fees.',
    companyType: 'A Reputed Women’s Apparel Store',
    companyCategory: 'Women’s Apparel & Activewear · Brookefields Mall',
    location: 'Coimbatore',
    fullLocation: 'Brookefields Mall, Coimbatore · On-site',
    type: 'Part-Time',
    postedDate: 'Recently Posted',
    isFeatured: true,
    brochureImage: '/jobs/part-time-sales-executive-coimbatore.jpeg',
    brochureTitle: 'Part-Time Sales Executive — Recruitment Flyer',
    brochureDownloadName: 'SkillVend_Part_Time_Sales_Executive_Coimbatore.jpeg',
    description: 'A reputed women’s apparel store at Brookefields Mall is hiring customer-focused, well-groomed part-time sales executives. You will help customers find the right products, keep the store looking its best, and contribute to the store’s sales targets. Open to women candidates; college students are welcome to apply.',
    highlights: [
      {
        icon: GraduationCap,
        label: 'Education',
        value: '10th / 12th Pass',
        subtext: 'Graduates and college students can also apply',
      },
      {
        icon: Briefcase,
        label: 'Experience',
        value: 'Freshers Welcome',
        subtext: 'Retail sales experience is an advantage',
      },
      {
        icon: Languages,
        label: 'Language',
        value: 'Tamil Essential',
        subtext: 'Good communication and customer-handling skills',
      },
      {
        icon: Clock,
        label: 'Working Hours',
        value: 'Part-Time Shifts',
        subtext: 'As per store schedule, including weekends and holidays',
      },
    ],
    responsibilities: [
      'Greeting customers and providing friendly, professional service.',
      'Understanding customer needs and recommending suitable products.',
      'Explaining product features, pricing, offers and promotions.',
      'Supporting sales conversion and working towards assigned sales targets.',
      'Maintaining product displays, folding, sizing and store cleanliness.',
      'Assisting with stock arrangement, replenishment and billing when required.',
      'Coordinating with the Store Manager and store staff, and following store policies.',
    ],
    applyEmail: 'helpdesk@skillvend.com',
    applySubject: 'Application - Part-Time Sales Executive, Brookefields Mall Coimbatore',
    applyPhone: '9847510101',
    whatsappMessage: 'Hi SkillVend Team, I am interested in applying for the Part-Time Sales Executive role at Brookefields Mall, Coimbatore.',
  },
  {
    id: 'licensing-regulatory-compliance-officer',
    title: 'Licensing & Regulatory Compliance Officer',
    category: 'compliance',
    headline: 'Good at compliance? This one’s for you.',
    companyType: 'A Leading Pharmacy Chain',
    companyCategory: 'One of India’s Leading Pharmacy & Retail Chains',
    openings: 1,
    location: 'Bangalore',
    fullLocation: 'Bangalore, Karnataka · On-site',
    type: 'Full-Time',
    postedDate: 'Recently Posted',
    isFeatured: true,
    brochureImage: '/jobs/licensing-compliance-officer.jpeg',
    brochureTitle: 'Licensing & Regulatory Compliance Officer — Recruitment Flyer',
    brochureDownloadName: 'SkillVend_Compliance_Officer_Brochure.jpeg',
    description: 'A pivotal regulatory leadership mandate for one of India’s largest and fastest-growing retail networks. You will manage statutory compliances, liaise with regulatory authorities, maintain licenses (FSSAI, Trade, Drugs & Cosmetics), and uphold operational standards across retail stores.',
    highlights: [
      {
        icon: GraduationCap,
        label: 'Education',
        value: 'Graduate',
        subtext: 'Pharmacy / Law / Commerce / Business preferred',
      },
      {
        icon: Briefcase,
        label: 'Experience',
        value: '2–5 Years',
        subtext: 'In Licensing, Regulatory Affairs or Statutory Compliance',
      },
      {
        icon: FileText,
        label: 'Regulatory Know-How',
        value: 'Statutory Licences',
        subtext: 'Trade Licence, FSSAI, Pharmacy Licence Know-How',
      },
      {
        icon: BarChart3,
        label: 'Core Skills',
        value: 'High Accuracy',
        subtext: 'Strong Follow-Up, Documentation & Excel Skills',
      },
    ],
    responsibilities: [
      'Managing fresh applications, renewals, and endorsements for Pharmacy, FSSAI, and Trade licenses.',
      'Liaising with municipal, state drug control, and food safety authorities in Karnataka.',
      'Conducting regular internal compliance audits across store and retail locations.',
      'Maintaining centralized statutory records, renewal trackers, and executive compliance dashboards in Excel.',
    ],
    applyEmail: 'helpdesk@skillvend.com',
    applySubject: 'Application - Licensing & Regulatory Compliance Officer',
    applyPhone: '9847510101',
    whatsappMessage: 'Hi SkillVend Team, I am interested in applying for the full-time Licensing & Regulatory Compliance Officer role in Bangalore. Please find my profile attached.',
  },
];

const Stamp = ({ children, tone = BRAND_COLORS.accentDeep, struck = false, className = '' }) => (
  <span
    className={`font-ledger inline-block uppercase whitespace-nowrap ${className}`}
    style={{
      color: tone,
      border: `${struck ? 2 : 1}px solid ${tone}`,
      padding: struck ? '4px 12px' : '3px 9px',
      fontSize: struck ? '0.72rem' : '0.62rem',
      letterSpacing: '0.14em',
      transform: struck ? 'rotate(-3deg)' : 'none',
    }}
  >
    {children}
  </span>
);

const JobsPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [brochureModalRole, setBrochureModalRole] = useState(null);

  useEffect(() => {
    // Deep links like /jobs#part-time-sales-executive-coimbatore land on that job card
    const target = window.location.hash && document.getElementById(window.location.hash.slice(1));
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!brochureModalRole) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape') setBrochureModalRole(null);
    };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [brochureModalRole]);

  const filteredJobs = JOB_OPPORTUNITIES.filter((job) => {
    const matchesCategory = selectedCategory === 'all' || job.category === selectedCategory;
    const matchesSearch =
      searchQuery.trim() === '' ||
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.companyCategory.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.location.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.headline.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: BRAND_COLORS.dark }}>
      {/* Sticky Top Header */}
      <header className="bg-black border-b sticky top-0 z-40" style={{ borderColor: HAIRLINE_GOLD }}>
        <div className="container mx-auto px-4 md:px-6 py-3.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="flex items-center gap-2 md:gap-3 group cursor-pointer"
              aria-label="SkillVend Home"
            >
              <BrandMark className="w-8 h-8 md:w-9 md:h-9 transition-transform group-hover:scale-105" />
              <div>
                <h1 className="text-sm md:text-base font-light tracking-widest" style={{ color: '#C9A961' }}>
                  SKILL<span className="font-extralight">VEND</span>
                </h1>
                <p className="font-ledger text-[0.58rem] tracking-[0.2em] text-[#B3B3AD] uppercase">
                  Jobs Portal
                </p>
              </div>
            </Link>
          </div>

          <nav className="flex items-center gap-4 sm:gap-6">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 font-ledger text-[0.65rem] tracking-[0.18em] uppercase text-gray-400 hover:text-[#C9A961] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Home</span>
            </Link>
            <a
              href="mailto:helpdesk@skillvend.com?subject=Jobs%20Inquiry"
              className="font-ledger text-[0.65rem] tracking-[0.18em] uppercase text-gray-400 hover:text-[#C9A961] transition-colors hidden md:inline"
            >
              Helpdesk
            </a>
            <a
              href="https://wa.me/919847510101?text=Hi%20Skillvend%2C%20I%20have%20an%20inquiry%20regarding%20job%20opportunities."
              target="_blank"
              rel="noopener noreferrer"
              className="px-3.5 py-1.5 border font-ledger text-[0.62rem] tracking-[0.15em] uppercase text-[#25D366] hover:bg-[#25D366]/10 transition-colors"
              style={{ borderColor: 'rgba(37, 211, 102, 0.4)' }}
            >
              WhatsApp Us
            </a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-black text-white pt-12 pb-14 md:pt-16 md:pb-20 border-b overflow-hidden" style={{ borderColor: HAIRLINE_GOLD }}>
        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 border bg-white/[0.03] mb-4" style={{ borderColor: HAIRLINE_GOLD }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_COLORS.accent }} />
              <span className="font-ledger text-[0.62rem] tracking-[0.25em] uppercase text-[#C9A961]">
                Permanent Mandates · Enterprise Placement
              </span>
            </div>

            <h2
              className="font-extralight leading-[1.05] tracking-tight text-white"
              style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)' }}
            >
              Job opportunities with
              <span className="block mt-1 text-[#C9A961]">India&apos;s leading enterprises.</span>
            </h2>

            <p className="mt-6 text-base md:text-lg font-light leading-relaxed text-gray-300 max-w-2xl">
              Beyond flexible daily shifts, SkillVend partners directly with pan-India leading retail brands and hospitality enterprises to place qualified professionals into high-impact, full-time jobs.
            </p>

            {/* Value Props Row */}
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-6 border-t pt-6" style={{ borderColor: HAIRLINE_WHITE }}>
              <div>
                <p className="font-ledger text-[0.6rem] tracking-[0.16em] uppercase text-gray-400">Candidate Fee</p>
                <p className="text-base font-medium text-[#C9A961] mt-0.5">₹0 · Always Free</p>
              </div>
              <div>
                <p className="font-ledger text-[0.6rem] tracking-[0.16em] uppercase text-gray-400">Positions</p>
                <p className="text-base font-medium text-white mt-0.5">Full-Time &amp; Part-Time</p>
              </div>
              <div>
                <p className="font-ledger text-[0.6rem] tracking-[0.16em] uppercase text-gray-400">Locations</p>
                <p className="text-base font-medium text-white mt-0.5">Pan-India</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <main className="flex-1 bg-[#0a0a0a] text-white py-10 md:py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">

          {/* Search & Filter Bar */}
          <div className="p-4 md:p-6 bg-[#111111] border mb-10" style={{ borderColor: HAIRLINE_WHITE }}>
            <div className="flex flex-col md:flex-row gap-4 items-stretch md:items-center justify-between">
              {/* Search input */}
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by role, skills, or city..."
                  className="w-full pl-10 pr-4 py-2.5 bg-black border text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#C9A961] font-light"
                  style={{ borderColor: 'rgba(255, 255, 255, 0.15)' }}
                />
              </div>

              {/* Category pills */}
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => {
                  const active = selectedCategory === cat.id;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className="font-ledger px-4 py-2 text-[0.62rem] tracking-[0.15em] uppercase border transition-colors"
                      style={{
                        backgroundColor: active ? BRAND_COLORS.accent : 'transparent',
                        color: active ? BRAND_COLORS.primary : '#B3B3AD',
                        borderColor: active ? BRAND_COLORS.accent : 'rgba(255, 255, 255, 0.15)',
                      }}
                    >
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 pt-3 border-t flex items-center justify-between text-xs text-gray-400 font-light" style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}>
              <span>
                Showing <strong className="text-white font-medium">{filteredJobs.length}</strong> active position{filteredJobs.length !== 1 ? 's' : ''}
              </span>
              <span className="font-ledger text-[0.58rem] tracking-[0.14em] uppercase text-[#A8A8A2]">
                Updated Daily
              </span>
            </div>
          </div>

          {/* Job Listings */}
          <div className="space-y-10">
            {filteredJobs.length === 0 ? (
              <div className="p-12 text-center border bg-[#111111]" style={{ borderColor: HAIRLINE_WHITE }}>
                <p className="text-lg font-light text-gray-300">No positions match your search criteria.</p>
                <p className="text-sm font-light text-gray-500 mt-2">Try clearing your search terms or category filters.</p>
                <button
                  onClick={() => { setSelectedCategory('all'); setSearchQuery(''); }}
                  className="mt-5 px-5 py-2.5 border font-ledger text-xs uppercase tracking-wider text-[#C9A961]"
                  style={{ borderColor: BRAND_COLORS.accent }}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              filteredJobs.map((role) => (
                <div
                  key={role.id}
                  id={role.id}
                  className="scroll-mt-20 bg-[#0e0e0e] border transition-all duration-300 hover:border-[#C9A961]/70"
                  style={{ borderColor: 'rgba(255, 255, 255, 0.16)' }}
                >
                  {/* Gold Top Accent */}
                  <div style={{ height: 4, backgroundColor: BRAND_COLORS.accent }} />

                  <div className="p-6 md:p-8 lg:p-10">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">

                      {/* Left Column: Brochure Preview & Quick Actions */}
                      <div className="lg:col-span-5 flex flex-col items-center">
                        <div className="w-full relative group">
                          <div
                            className="relative overflow-hidden border bg-black shadow-2xl cursor-pointer"
                            style={{ borderColor: HAIRLINE_GOLD }}
                            onClick={() => setBrochureModalRole(role)}
                            title="Click to view full recruitment flyer"
                          >
                            <img
                              src={role.brochureImage}
                              alt={role.title + ' Brochure'}
                              className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                            />
                            <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                              <div className="px-4 py-2.5 bg-black/90 border border-[#C9A961] text-white flex items-center gap-2 text-xs font-light tracking-widest uppercase">
                                <Maximize2 className="w-4 h-4 text-[#C9A961]" />
                                <span>View Full Flyer</span>
                              </div>
                            </div>
                            <span
                              className="absolute top-3 left-3 px-2.5 py-1 font-ledger text-[0.58rem] tracking-[0.16em] uppercase bg-black/90 border"
                              style={{ borderColor: BRAND_COLORS.accent, color: BRAND_COLORS.accent }}
                            >
                              Recruitment Flyer
                            </span>
                          </div>

                          {/* Quick Flyer Actions */}
                          <div className="mt-4 grid grid-cols-2 gap-3">
                            <button
                              onClick={() => setBrochureModalRole(role)}
                              className="flex items-center justify-center gap-2 px-3 py-2.5 border text-xs font-light tracking-wider uppercase transition-colors hover:bg-white hover:text-black"
                              style={{ borderColor: 'rgba(255, 255, 255, 0.25)', color: '#FFFFFF' }}
                            >
                              <Maximize2 className="w-3.5 h-3.5 text-[#C9A961]" />
                              <span>Preview</span>
                            </button>
                            <a
                              href={role.brochureImage}
                              download={role.brochureDownloadName}
                              className="flex items-center justify-center gap-2 px-3 py-2.5 border text-xs font-light tracking-wider uppercase transition-colors hover:bg-[#C9A961] hover:text-black"
                              style={{ borderColor: BRAND_COLORS.accent, color: BRAND_COLORS.accent }}
                            >
                              <Download className="w-3.5 h-3.5" />
                              <span>Download</span>
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Right Column: Detailed Role Requirements */}
                      <div className="lg:col-span-7 flex flex-col justify-between">
                        <div>
                          {/* Badges */}
                          <div className="flex flex-wrap items-center gap-2.5">
                            {role.openings && (
                              <Stamp tone={BRAND_COLORS.accent}>
                                {role.openings} Opening{role.openings !== 1 ? 's' : ''}
                              </Stamp>
                            )}
                            <Stamp tone="#25D366">{role.type}</Stamp>
                            <span className="flex items-center gap-1.5 font-ledger text-[0.65rem] tracking-[0.15em] uppercase text-[#B3B3AD]">
                              <MapPin className="w-3.5 h-3.5 text-[#C9A961]" />
                              {role.fullLocation}
                            </span>
                            <span className="font-ledger text-[0.62rem] tracking-[0.14em] uppercase text-gray-400">
                              {role.postedDate}
                            </span>
                          </div>

                          {/* Category & Title */}
                          <p className="font-ledger text-[0.7rem] tracking-[0.2em] uppercase mt-4 text-[#C9A961]">
                            {role.companyType}
                          </p>
                          <h3 className="text-2xl sm:text-3xl font-light tracking-tight text-white mt-1">
                            {role.title}
                          </h3>
                          <p className="font-ledger text-[0.72rem] tracking-[0.14em] uppercase text-gray-400 mt-1">
                            {role.companyCategory}
                          </p>

                          {/* Headline Quote */}
                          <div
                            className="mt-5 p-4 border-l-2 bg-white/[0.03]"
                            style={{ borderColor: BRAND_COLORS.accent }}
                          >
                            <p className="text-base sm:text-lg font-light text-gray-200 italic">
                              &ldquo;{role.headline}&rdquo;
                            </p>
                            <p className="text-xs font-light text-gray-400 mt-1.5 leading-relaxed">
                              {role.description}
                            </p>
                          </div>

                          {/* 4 Qualification Cards */}
                          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {role.highlights.map((item) => {
                              const ItemIcon = item.icon;
                              return (
                                <div
                                  key={item.label}
                                  className="p-4 border bg-white/[0.02] flex items-start gap-3.5"
                                  style={{ borderColor: 'rgba(255, 255, 255, 0.08)' }}
                                >
                                  <div
                                    className="w-9 h-9 flex items-center justify-center shrink-0 border"
                                    style={{ borderColor: HAIRLINE_GOLD, backgroundColor: 'rgba(201, 169, 97, 0.1)' }}
                                  >
                                    <ItemIcon className="w-4 h-4 text-[#C9A961]" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className="font-ledger text-[0.6rem] tracking-[0.16em] uppercase text-[#A8A8A2]">
                                      {item.label}
                                    </p>
                                    <p className="text-sm font-medium text-white tracking-wide mt-0.5">
                                      {item.value}
                                    </p>
                                    <p className="text-xs font-light text-gray-400 mt-1 leading-snug">
                                      {item.subtext}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Responsibilities */}
                          <div className="mt-6 pt-5 border-t" style={{ borderColor: HAIRLINE_WHITE }}>
                            <p className="font-ledger text-[0.65rem] tracking-[0.2em] uppercase text-gray-400 mb-3">
                              Key Scope of Work
                            </p>
                            <ul className="space-y-2">
                              {role.responsibilities.map((resp, idx) => (
                                <li key={idx} className="flex items-start gap-2.5 text-xs text-gray-300 font-light leading-relaxed">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9A961] mt-1.5 shrink-0" />
                                  <span>{resp}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Apply Box */}
                        <div
                          className="mt-8 p-6 border bg-white/[0.03]"
                          style={{ borderColor: HAIRLINE_GOLD }}
                        >
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-4 border-b" style={{ borderColor: HAIRLINE_WHITE }}>
                            <div>
                              <p className="font-ledger text-[0.65rem] tracking-[0.2em] uppercase text-[#C9A961]">
                                Direct Application
                              </p>
                              <h4 className="text-lg font-light text-white tracking-wide mt-0.5">
                                Send us your resume today
                              </h4>
                            </div>
                            <span className="font-ledger text-[0.62rem] tracking-[0.14em] uppercase text-[#25D366] flex items-center gap-1.5">
                              <ShieldCheck className="w-3.5 h-3.5" />
                              SkillVend Verified Mandate
                            </span>
                          </div>

                          {/* CTAs */}
                          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                            <a
                              href={`https://wa.me/91${role.applyPhone}?text=${encodeURIComponent(role.whatsappMessage)}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center justify-center gap-3 px-5 py-3.5 bg-[#25D366] hover:bg-[#20bd5a] text-black font-medium text-xs tracking-wider uppercase transition-colors"
                            >
                              <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24">
                                <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                              </svg>
                              <span>WhatsApp Resume</span>
                            </a>

                            <a
                              href={`mailto:${role.applyEmail}?subject=${encodeURIComponent(role.applySubject)}`}
                              className="flex items-center justify-center gap-3 px-5 py-3.5 bg-white hover:bg-[#C9A961] text-black font-medium text-xs tracking-wider uppercase transition-colors"
                            >
                              <Mail className="w-4 h-4 shrink-0" />
                              <span>Email Resume</span>
                            </a>
                          </div>

                          <p className="mt-3.5 text-center sm:text-left font-ledger text-[0.62rem] tracking-[0.12em] text-[#A8A8A2] uppercase">
                            Send CV to <a href={`mailto:${role.applyEmail}`} className="text-white underline">{role.applyEmail}</a> or WhatsApp <span className="text-white">+91 {role.applyPhone}</span>
                          </p>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Talent Pool Card: For all future & open applications */}
            <div
              className="p-8 md:p-10 border bg-[#111111] relative overflow-hidden"
              style={{ borderColor: HAIRLINE_GOLD }}
            >
              <div className="max-w-3xl">
                <div className="flex items-center gap-3 mb-3">
                  <Users className="w-5 h-5 text-[#C9A961]" />
                  <p className="font-ledger text-[0.65rem] tracking-[0.22em] uppercase text-[#C9A961]">
                    Open Talent Network · All Domains
                  </p>
                </div>
                <h3 className="text-xl sm:text-2xl font-light text-white tracking-wide">
                  Don&apos;t see a matching role for your profile?
                </h3>
                <p className="text-sm font-light text-gray-300 mt-3 leading-relaxed">
                  SkillVend receives new enterprise mandates weekly across Store Operations, Retail Leadership,
                  Statutory Affairs, and Hospitality Management. Send your resume to our general talent pool and our placement desk will contact you as matching positions open.
                </p>

                <div className="mt-6 flex flex-wrap gap-4">
                  <a
                    href="mailto:helpdesk@skillvend.com?subject=General%20Candidate%20Resume%20Submission"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9A961] hover:bg-white text-black font-medium text-xs tracking-wider uppercase transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    <span>Submit CV to Talent Pool</span>
                  </a>
                  <a
                    href="https://wa.me/919847510101?text=Hi%20Skillvend%2C%20I%20would%20like%20to%20register%20my%20profile%20for%20future%20full-time%20job%20opportunities."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 border border-white/25 text-white hover:bg-white/10 font-medium text-xs tracking-wider uppercase transition-colors"
                  >
                    <span>Connect on WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>

            {/* Employer Hiring Callout */}
            <div
              className="p-8 border bg-white/[0.02] flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
              style={{ borderColor: HAIRLINE_WHITE }}
            >
              <div className="flex items-start gap-4">
                <div
                  className="w-10 h-10 flex items-center justify-center border shrink-0"
                  style={{ borderColor: HAIRLINE_GOLD, backgroundColor: 'rgba(201, 169, 97, 0.1)' }}
                >
                  <Sparkles className="w-5 h-5 text-[#C9A961]" />
                </div>
                <div>
                  <h4 className="text-base font-light text-white tracking-wide">
                    Are you an employer hiring permanent talent?
                  </h4>
                  <p className="text-sm font-light text-gray-400 mt-1 max-w-xl">
                    Partner with SkillVend to recruit verified statutory compliance officers, retail leads, and operations managers across India.
                  </p>
                </div>
              </div>

              <a
                href="mailto:helpdesk@skillvend.com?subject=Employer%20Recruitment%20Partnership%20Inquiry"
                className="px-6 py-3 border font-ledger text-[0.65rem] tracking-[0.2em] uppercase text-white hover:bg-white hover:text-black transition-colors whitespace-nowrap"
                style={{ borderColor: BRAND_COLORS.accent }}
              >
                Hire with SkillVend
              </a>
            </div>

          </div>
        </div>
      </main>

      {/* Brochure High-Resolution Lightbox Modal */}
      {brochureModalRole && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex flex-col justify-between select-none animate-fade-in backdrop-blur-sm"
          onClick={() => setBrochureModalRole(null)}
        >
          {/* Top Bar */}
          <div
            className="flex items-center justify-between px-6 py-4 border-b z-10 bg-black/80"
            style={{ borderColor: HAIRLINE_WHITE }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: BRAND_COLORS.accent }} />
              <div>
                <span className="font-ledger text-[0.6rem] tracking-[0.25em] uppercase text-[#C9A961]">
                  Official Recruitment Flyer
                </span>
                <p className="font-ledger text-[0.65rem] tracking-[0.15em] uppercase text-white mt-0.5">
                  {brochureModalRole.title} · {brochureModalRole.location}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <a
                href={brochureModalRole.brochureImage}
                download={brochureModalRole.brochureDownloadName}
                className="p-2 text-gray-400 hover:text-white border border-white/15 hover:border-white/40 transition-colors flex items-center gap-2 text-xs font-ledger tracking-wider uppercase px-3"
                title="Download flyer"
              >
                <Download className="w-4 h-4 text-[#C9A961]" />
                <span className="hidden sm:inline">Download</span>
              </a>
              <button
                onClick={() => setBrochureModalRole(null)}
                aria-label="Close Brochure Modal"
                className="p-2 text-gray-400 hover:text-white border border-white/15 hover:border-white/40 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Centered Image Viewer */}
          <div
            className="flex-1 relative flex items-center justify-center p-4 min-h-0 overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative max-w-2xl max-h-[80vh] w-full flex items-center justify-center">
              <img
                src={brochureModalRole.brochureImage}
                alt={brochureModalRole.brochureTitle}
                className="max-w-full max-h-[78vh] object-contain border shadow-2xl"
                style={{ borderColor: HAIRLINE_GOLD }}
              />
            </div>
          </div>

          {/* Bottom Bar */}
          <div
            className="border-t py-4 px-6 text-center z-10 bg-black/80 flex flex-col sm:flex-row items-center justify-between gap-4"
            style={{ borderColor: HAIRLINE_WHITE }}
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-ledger text-[0.62rem] tracking-[0.16em] uppercase text-gray-400">
              Apply via resume to <a href="mailto:helpdesk@skillvend.com" className="text-white underline">helpdesk@skillvend.com</a> or WhatsApp <span className="text-[#25D366]">+91 9847510101</span>
            </p>
            <div className="flex items-center gap-3">
              <a
                href={`https://wa.me/91${brochureModalRole.applyPhone}?text=${encodeURIComponent(brochureModalRole.whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-[#25D366] text-black font-medium text-[0.68rem] tracking-wider uppercase"
              >
                WhatsApp Application
              </a>
              <button
                onClick={() => setBrochureModalRole(null)}
                className="px-4 py-2 border border-white/20 text-gray-300 hover:text-white font-ledger text-[0.65rem] tracking-wider uppercase"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-black text-white border-t" style={{ borderColor: HAIRLINE_WHITE }}>
        <div className="container mx-auto px-6 max-w-7xl py-11">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <Link
                to="/"
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="flex items-center gap-4 group cursor-pointer"
                aria-label="SkillVend Home"
              >
                <BrandMark className="w-10 h-10 transition-transform group-hover:scale-105" />
                <div>
                  <p className="text-sm font-light tracking-widest" style={{ color: '#C9A961' }}>
                    SKILL<span className="font-extralight">VEND</span>
                  </p>
                  <p className="font-ledger text-[0.6rem] tracking-[0.15em] text-[#A8A8A2] mt-1 uppercase">Est. 2021 · Bengaluru</p>
                </div>
              </Link>
              <p className="mt-5 text-sm font-light leading-relaxed text-[#B3B3AD] max-w-sm">
                Empowering employability through flexible shift work and full-time career placements.
              </p>
            </div>

            <div className="md:col-span-3">
              <p className="font-ledger text-[0.65rem] tracking-[0.2em] uppercase text-[#A8A8A2]">Navigation</p>
              <ul className="mt-5 space-y-3 text-sm font-light text-gray-300">
                <li><Link to="/" className="hover:text-[#C9A961] transition-colors">SkillVend Home</Link></li>
                <li><Link to="/jobs" className="text-[#C9A961]">Jobs Portal</Link></li>
                <li><a href="/#employers" className="hover:text-[#C9A961] transition-colors">For Employers</a></li>
                <li><a href="/#faq" className="hover:text-[#C9A961] transition-colors">FAQ</a></li>
              </ul>
            </div>

            <div className="md:col-span-4">
              <p className="font-ledger text-[0.65rem] tracking-[0.2em] uppercase text-[#A8A8A2]">Contact</p>
              <ul className="mt-5 space-y-3 text-sm font-light text-gray-300">
                <li>Email: <a href="mailto:helpdesk@skillvend.com" className="hover:text-[#C9A961]">helpdesk@skillvend.com</a></li>
                <li>WhatsApp: <a href="https://wa.me/919847510101" target="_blank" rel="noopener noreferrer" className="hover:text-[#C9A961]">+91 9847510101</a></li>
                <li><Link to="/legal#terms" className="hover:text-[#C9A961]">Terms of Service</Link></li>
                <li><Link to="/legal#privacy" className="hover:text-[#C9A961]">Privacy Policy</Link></li>
              </ul>
            </div>
          </div>

          <div className="mt-9 pt-6 border-t flex flex-col sm:flex-row justify-between gap-3 text-[#A8A8A2]" style={{ borderColor: HAIRLINE_WHITE }}>
            <p className="font-ledger text-[0.62rem] tracking-[0.15em] uppercase">
              © 2021–2026 SkillVend Solutions Private Limited. All rights reserved.
            </p>
            <p className="font-ledger text-[0.62rem] tracking-[0.15em] uppercase">
              Empowering Employability
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default JobsPage;
